import React, { useEffect, useState } from 'react';
import { Box, SpaceBetween, Modal, FormField, Button, Input, Alert } from '@amzn/awsui-components-react';
import StringUtils from 'src/utils/stringUtils';
import CreateMultiAllocationWorksheetRequest from 'src/models/multidimensional-allocation/CreateMultiAllocationWorksheetRequest';

interface CreateWorksheetModalProps {
    visible: boolean;
    isCreating: boolean;
    createError: string;
    onClose: () => void;
    onSubmit: (request: CreateMultiAllocationWorksheetRequest) => void;
}

/**
 * Modal component for creating new multidimensional allocation worksheets
 * Provides form validation and error handling for worksheet creation
 */
export default function CreateWorksheetModal(props: CreateWorksheetModalProps) {
    const { visible, isCreating, createError, onClose, onSubmit } = props;

    const [worksheetName, setWorksheetName] = useState('');
    const [nameValidationError, setNameValidationError] = useState('');

    // Reset form when modal opens
    useEffect(() => {
        if (visible) {
            setWorksheetName('');
            setNameValidationError('');
        }
    }, [visible]);

    // Worksheet name validation rules
    const validateWorksheetName = (name: string): string => {
        if (StringUtils.isNullOrEmpty(name)) {
            return 'Worksheet name is required';
        }

        // Character validation - allow letters, numbers, spaces, hyphens, underscores, parentheses
        const validCharacterPattern = /^[a-zA-Z0-9\s\-_()]+$/;
        if (!validCharacterPattern.test(name)) {
            return 'Worksheet name can only contain letters, numbers, spaces, hyphens, underscores, and parentheses';
        }

        // Cannot be only spaces or special characters
        if (!/[a-zA-Z0-9]/.test(name)) {
            return 'Worksheet name must contain at least one letter or number';
        }

        return '';
    };

    // Handle input change with immediate validation
    const handleNameChange = (newName: string) => {
        const validationError = validateWorksheetName(newName);
        setWorksheetName(newName);
        setNameValidationError(validationError);
    };

    const isCreateDisabled = StringUtils.isNullOrEmpty(worksheetName) || !StringUtils.isNullOrEmpty(nameValidationError);

    const handleSubmit = () => {
        const createWorksheetRequest: CreateMultiAllocationWorksheetRequest = {
            worksheetName: worksheetName
        };
        onSubmit(createWorksheetRequest);
    };

    return (
        <Modal
            data-class="createWorksheetModal"
            visible={visible}
            header="Create worksheet"
            size="medium"
            onDismiss={onClose}
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button 
                            variant="link" 
                            className="cancelButton" 
                            disabled={isCreating} 
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        <Button 
                            variant="primary" 
                            className="createButton" 
                            disabled={isCreating || isCreateDisabled} 
                            onClick={handleSubmit}
                        >
                            {isCreating ? 'Creating...' : 'Create'}
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween direction="vertical" size="m">
                <Box>
                    Do you want to create a worksheet?
                </Box>
                <FormField
                    data-class="createModalField"
                    label="Worksheet name"
                    stretch={false}
                    description="Enter a unique name for your allocation worksheet"
                    errorText={nameValidationError}
                >
                    <Input
                        data-class="basicModalInput worksheetNameFieldInput"
                        value={worksheetName}
                        onChange={e => handleNameChange(e.detail.value)}
                        placeholder="Multidimensional allocation worksheet Name"
                        ariaLabel="Worksheet name input"
                        autoFocus
                        invalid={!StringUtils.isNullOrEmpty(nameValidationError)}
                    />
                </FormField>
                {createError && !StringUtils.isNullOrEmpty(createError) && (
                    <Alert type="error" dismissible={false}>
                        {createError}
                    </Alert>
                )}
            </SpaceBetween>
        </Modal>
    );
}
